import {
  type ColumnDef,
  DataGrid,
  DataGridColumnHeader,
  type PaginationState,
} from "@atlas/core";
import { useTranslation } from "@atlas/sdk";
import { useMemo } from "react";
import { truncatingHeaderTitle } from "./truncatingHeaderTitle";
import type { IUsageRow } from "./types";

const DATA_GRID_LAYOUT = {
  columnsVisibility: true,
  headerBackground: true,
  tableBorder: true,
} as const;

interface IUsageTabProps {
  data: IUsageRow[];
  totalElements?: number;
  totalPages?: number;
  pagination: PaginationState;
  onPaginationChange: (
    updater: PaginationState | ((old: PaginationState) => PaginationState),
  ) => void;
  loading?: boolean;
}

export function UsageTab({
  data: usageData,
  totalElements = 0,
  totalPages = 0,
  pagination,
  onPaginationChange,
  loading = false,
}: IUsageTabProps) {
  const { t } = useTranslation();

  const usageColumns = useMemo<ColumnDef<IUsageRow, unknown>[]>(
    () => [
      {
        accessorKey: "subscribedTo",
        header: (info) => (
          <DataGridColumnHeader
            column={info.column}
            title={t("hub.subscription.subscribed_to")}
          />
        ),
        cell: (info) => String(info.getValue() ?? "—"),
      },
      {
        accessorKey: "softwareStartDate",
        header: (info) => (
          <DataGridColumnHeader
            column={info.column}
            title={t("hub.subscription.software_start_date")}
          />
        ),
        cell: (info) => {
          const val = info.getValue() as string;
          return val ? val.split("T")[0] : "\u2014";
        },
      },
      {
        accessorKey: "softwareEndDate",
        header: (info) => (
          <DataGridColumnHeader
            column={info.column}
            title={t("hub.subscription.software_end_date")}
          />
        ),
        cell: (info) => {
          const val = info.getValue() as string;
          return val ? val.split("T")[0] : "\u2014";
        },
      },
      {
        accessorKey: "subscriptionSourceBillCode",
        minSize: 160,
        header: (info) => (
          <DataGridColumnHeader
            column={info.column}
            className="min-w-0 max-w-full gap-1"
            title={truncatingHeaderTitle(
              t("hub.subscription.subscription_source_bill_code"),
            )}
          />
        ),
        cell: (info) => String(info.getValue() ?? "—"),
      },
      {
        accessorKey: "subscriptionInvoiceBillCode",
        minSize: 160,
        header: (info) => (
          <DataGridColumnHeader
            column={info.column}
            className="min-w-0 max-w-full gap-1"
            title={truncatingHeaderTitle(
              t("hub.subscription.subscription_invoice_bill_code"),
            )}
          />
        ),
        cell: (info) => String(info.getValue() ?? "—"),
      },
      {
        accessorKey: "activeUsers",
        header: (info) => (
          <DataGridColumnHeader
            column={info.column}
            title={t("hub.subscription.active_users")}
          />
        ),
        cell: (info) => String(info.getValue() ?? "—"),
      },
      {
        accessorKey: "storage",
        header: (info) => (
          <DataGridColumnHeader
            column={info.column}
            title={t("hub.catalog_subscribe.line_storage")}
          />
        ),
        cell: (info) => String(info.getValue() ?? "—"),
      },
      {
        accessorKey: "aiCredits",
        header: (info) => (
          <DataGridColumnHeader
            column={info.column}
            title={t("hub.subscription.ai_credits")}
          />
        ),
        cell: (info) => String(info.getValue() ?? "—"),
      },
      {
        accessorKey: "contentCredits",
        header: (info) => (
          <DataGridColumnHeader
            column={info.column}
            title={t("hub.subscription.content_credits")}
          />
        ),
        cell: (info) => String(info.getValue() ?? "—"),
      },
      {
        accessorKey: "apiGateway",
        meta: { headerTitle: t("hub.subscription.api_gateway") },
        header: (info) => (
          <DataGridColumnHeader
            column={info.column}
            title={t("hub.subscription.api_gateway")}
          />
        ),
        cell: (info) => String(info.getValue() ?? "—"),
      },
    ],
    [t],
  );

  return (
    <DataGrid<IUsageRow>
      data={usageData}
      columns={usageColumns}
      recordCount={totalElements}
      tableLayout={DATA_GRID_LAYOUT}
      withExport
      withSearch={{
        searchKey: "subscribedTo",
        placeholder: t("hub.subscription.search_results"),
      }}
      paginationPlacement="toolbar"
      manualPagination
      pageCount={totalPages}
      pagination={pagination}
      onPaginationChange={onPaginationChange}
      loading={loading}
      emptyMessage={t("hub.subscription.no_usage_found")}
    />
  );
}
